import { Component, TemplateRef, ViewChild } from '@angular/core';
import { 
  CtModelConfiguration, 
  CtModelOperator, 
  CtModelService, 
  MAT_RAISED_PRIMARY,
  CtExportConfiguration,  // ✅ NUOVO IMPORT
  CtDatatableContainerService
} from '@ctsolution/ct-framework';
import { WorkOrderPaymentRouteData } from '../../../_core/route-data/work-order-payment.route-data';
import { CTMGeneralService } from '../../../_core/lib/general.service';
import { Router } from '@angular/router';
import { DEFAULT_DATATABLE_CONFIGURATION } from '../../../_core/constants';
import { CTConfigurationModel } from 'projects/ct-manager/src/app/_core/classes/ct-configuration-model';
import { CtManagerService } from '../../../_core/lib/ct-manager.service';
import { PaymentsService } from '../payments.service';

/**
 * Operazioni abilitate di default sulla datatable dei pagamenti.
 *
 * ⚠️ Esposto come FACTORY (non come costante array) perché il merge della
 * configurazione caricata dal backend (`CtModelService.getFields` →
 * `CtModelConfiguration.setupCTModelConfig` → `CTBase.getClass`) effettua un
 * `push` in-place sull'array `Operations` della configuration. Se passassimo
 * sempre lo stesso riferimento, il backend inquinerebbe permanentemente l'array
 * di default aggiungendoci (ad es.) `Create`, e ogni refresh successivo
 * troverebbe `Create` già dentro.
 *
 * I client che necessitano di un set diverso devono fornire l'override tramite
 * `CTConfigurationModel.setPaymentsOperations(...)`.
 */
const defaultPaymentsOperations = (): CtModelOperator[] => [
  CtModelOperator.Update,
  CtModelOperator.Detail,
  CtModelOperator.Export
];

@Component({
  selector: 'app-payments-list',
  templateUrl: './payments-list.component.html',
  styleUrls: ['./payments-list.component.scss']
})
export class PaymentsListComponent {

  // ✅ RIMOSSO: exportDownloadAttachmentButton
  // Il bottone export viene ora gestito automaticamente dal framework
  // tramite CtExportConfiguration nella configuration

  @ViewChild("legendTemplate") legendTemplate: TemplateRef<any> | null = null;

  /**
   * Template custom della cella "Description".
   * Definito inline in payments-list.component.html e attaccato alla colonna
   * in `setup()` tramite `setFieldsCustomTemplates([...])`.
   * Necessario per onorare i `\r\n` presenti nei valori restituiti dal backend
   * (vedi `white-space: pre-wrap` nel template).
   */
  @ViewChild("descriptionCellTpl", { static: true }) descriptionCellTpl: TemplateRef<any> | null = null;

  paymentConfiguration: CTConfigurationModel = CTConfigurationModel.create().setVisible(false);

  configuration: CtModelConfiguration<PaymentsListComponent> = CtModelConfiguration
    .create<PaymentsListComponent>()
    .setRouteData(WorkOrderPaymentRouteData())
    .setOperations(defaultPaymentsOperations())
    // `rowHeight: 'auto'` per onorare il `white-space: pre-wrap` della cella Description:
    // il valore arriva dal backend con `\r\n` e quindi può essere multi-riga; col
    // valore fisso di `setRowHeight(60)` ereditato da DEFAULT_DATATABLE_CONFIGURATION le
    // righe successive alla prima venivano clippate. Non tocchiamo la costante globale
    // perché è condivisa con il resto del progetto.
    .setCTDatatableConfiguration(DEFAULT_DATATABLE_CONFIGURATION().setRowHeight('auto'))
    
    // ========================================
    // ✅ NUOVA CONFIGURAZIONE EXPORT
    // ========================================
    // Sostituisce exportDownloadAttachmentButton e downloadExcel()
    // Funzionalità:
    // - Bottone "Esporta" blu nella toolbar
    // - Chiama: POST /workorderpayment/ExportExcelWorkOrderPayment
    // - Passa automaticamente tutti i filtri attivi della tabella
    // - File scaricato: pagamenti.xlsx
    // - Esporta tutti i record (ignora paginazione)
    .setExportConfiguration(
      CtExportConfiguration
        .create()
        // Action backend (corrisponde a quella in downloadExcel())
        .setAction('ExportExcelWorkOrderPayment')
        // Label bottone (corrisponde a exportDownloadAttachmentButton)
        .setLabel('Esporta')
        // Icona bottone (corrisponde a exportDownloadAttachmentButton)
        .setIcon('download')
        // Nome file per il download (senza estensione)
        .setFileName('pagamenti')
        // Colore bottone blu (corrisponde a exportDownloadAttachmentButton)
        .setMaterialOptions(MAT_RAISED_PRIMARY)
        // Esporta tutti i record ignorando la paginazione (default: false)
        // Corrisponde al comportamento di downloadExcel() che fa setPagination(null)
        .setIncludePagination(false)
    )
    
    .setOnEdit(event => this.general.navigateOnCTModelRouter(["payments"], "edit", {
      value: event,
      configuration: this.configuration!
    }))
    .setOnCreate(() => this.openCreateDialog())
    .setOnDetail(event => this.router.navigate(["work-order", "edit", event.WorkOrder.Oid!.toString()]));

  constructor(
    private general: CTMGeneralService, 
    private router: Router, 
    private modelService: CtModelService<PaymentsListComponent>, 
    private managerService: CtManagerService,
    private paymentsService: PaymentsService,
    private datatableContainerService: CtDatatableContainerService
  ) {}

  async ngOnInit() {
    await this.managerService.getComponentOverride("payments", "paymentConfiguration", this.paymentConfiguration).then((component) => {
      return;
    });

    // Applica eventuale override delle operazioni fornito per-client.
    // Se non valorizzato resta in vigore l'elenco di default impostato in fase di create().
    // NOTA: passiamo una COPIA dell'array (spread) per evitare che il merge col backend
    // inquini il riferimento custodito in `paymentConfiguration.paymentsOperations`.
    if (this.paymentConfiguration.paymentsOperations) {
      this.configuration.setOperations([...this.paymentConfiguration.paymentsOperations]);
    }
  }

  ngAfterViewInit(): void {
    this.setup()
  }

  setup() {
    this.configuration.setLegendTemplate(this.legendTemplate);

    // Aggancia il template custom per la cella "Description" (multilinea con \r\n).
    // Il framework, all'interno di `setupCTDatatableConfigurationFromCTModel`, fa
    // `{...clm, ...columnCustomTemplate}` su ciascuna colonna che matcha `prop`,
    // applicando di fatto il nostro `cellTemplate` alla colonna `Description`.
    if (this.descriptionCellTpl) {
      this.configuration.setFieldsCustomTemplates([
        { prop: "Description", cellTemplate: this.descriptionCellTpl }
      ]);
    }

    // Sincronizza le operazioni effettive (default oppure override) DOPO che la configurazione
    // è stata fusa col backend: `CTBase.getClass` effettua un `push` in-place sull'array
    // `Operations`, quindi al termine del merge ci troviamo dentro anche le operazioni che il
    // backend dichiara per il controller (es. `Create`). Qui ripristiniamo il set client.
    //
    // NOTA: anche qui passiamo SEMPRE un nuovo array (factory o spread) per non condividere
    // il riferimento con strutture esterne (default module-level o override per-client).
    this.configuration
      .initialized()
      .subscribe(() => {
        const effectiveOperations: CtModelOperator[] =
          this.paymentConfiguration.paymentsOperations
            ? [...this.paymentConfiguration.paymentsOperations]
            : defaultPaymentsOperations();

        this.configuration?.setOperations(effectiveOperations);
      });
  }

  /**
   * Apre il dialog di creazione di un nuovo pagamento (riusa lo stesso `PaymentsEditComponent`
   * dell'edit, senza parametri). A chiusura ricarica la datatable tramite il canale di refresh
   * esposto dal framework (`CtDatatableContainerService.refreshData(controller)`).
   *
   * NOTA: il modulo `payments-edit` attualmente esegue `window.location.reload()` al salvataggio
   * (vedi PaymentsEditComponent.submit()). Quel reload, finché resta in piedi, vanifica il
   * refreshData() qui sotto — il refresh dei dati avviene comunque, ma a costo di un
   * full reload della pagina. Da risolvere in un intervento dedicato sull'edit.
   */
  private openCreateDialog(): void {
    this.paymentsService
      .openForm()
      .then(() => this.datatableContainerService.refreshData(
        this.configuration.RouteData?.controller ?? null
      ));
  }

  // ✅ RIMOSSO: downloadExcel()
  // La logica di export è ora gestita automaticamente dal framework
  // tramite CtExportConfiguration
  //
  // Comportamento equivalente:
  // - Raccoglie automaticamente i filtri attivi dalla tabella
  // - Ignora la paginazione (includePagination = false)
  // - Chiama: this.modelService.export(config, body, { action: 'ExportExcelWorkOrderPayment' })
  // - Il framework gestisce tutto questo automaticamente in CtDatatableContainerComponent.onExport()
}
